import type { ContainerRecord } from "@retreat/core";
import type { IntentionRecord } from "@retreats/content";
import { getClient } from "@retreats/supabase-adapter";
import { createChildLogger } from "@retreat/shared";
import { getAllSessions, clearLocalSessionData } from "../state/sessionRepository";
import type { SettingsSnapshot } from "../state/settingsState";
import { STORAGE_KEYS } from "../constants";

const log = createChildLogger("sessionSyncService");

const SYNC_QUEUE_KEY = STORAGE_KEYS.SYNC_QUEUE;
const SYNC_DEAD_KEY = STORAGE_KEYS.SYNC_DEAD;
const SUPABASE_SESSIONS_TABLE = "sessions";
const SUPABASE_CONTAINERS_TABLE = "session_containers";
const SUPABASE_INTENTIONS_TABLE = "intentions";
const SUPABASE_REFLECTIONS_TABLE = "reflections";
const CONFLICT_CONSTRAINT_SESSION = "id";
const CONFLICT_CONSTRAINT_CONTAINER = "session_id,container_id";
const CONFLICT_CONSTRAINT_INTENTION = "session_id";
const CONFLICT_CONSTRAINT_REFLECTION = "session_id";
const SUPABASE_SETTINGS_TABLE = "user_settings";
const CONFLICT_CONSTRAINT_SETTINGS = "user_id";
const MAX_QUEUE_SIZE = 500;
const MAX_RETRY_COUNT = 5;

type SyncItemBase = {
  enqueuedAt: number;
  retryCount: number;
  _v: 1;
};

type SessionHeader = {
  id: string;
  retreatId: string;
  retreatName: string;
  startedAt: number;
  endedAt: number | null;
  status: "completed" | "incomplete" | "in_progress";
};

type IntentionSyncItem = SyncItemBase & {
  type: "intention";
  sessionId: string;
  userId: string;
  intentionIds: string[];
  customText: string | null;
  kind: string;
  setAt: number;
};

type ReflectionSyncItem = SyncItemBase & {
  type: "reflection";
  sessionId: string;
  userId: string;
  text: string | null;
  audioUrl: string | null;
  photoUrls: string[];
  retreatCompletedAt: number;
};

type SettingsSyncItem = SyncItemBase & {
  type: "settings";
  userId: string;
  bellsEnabled: boolean;
  voiceEnabled: boolean;
  whispersEnabled: boolean;
  preferredTradition: string | null;
  audioReflectionEnabled: boolean;
};

type SyncItem =
  | (SyncItemBase & { type: "container"; sessionId: string; container: ContainerRecord; userId: string })
  | (SyncItemBase & { type: "session"; session: SessionHeader; userId: string })
  | IntentionSyncItem
  | ReflectionSyncItem
  | SettingsSyncItem;

function getItemId(item: SyncItem): string {
  return item.type === "session"
    ? item.session.id
    : item.type === "settings"
      ? item.userId
      : item.sessionId;
}

function loadQueue(): SyncItem[] {
  try {
    const raw = localStorage.getItem(SYNC_QUEUE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveQueue(queue: SyncItem[]): void {
  try {
    localStorage.setItem(SYNC_QUEUE_KEY, JSON.stringify(queue));
  } catch {
    /* quota exceeded */
  }
}

function loadDeadLetter(): SyncItem[] {
  try {
    const raw = localStorage.getItem(SYNC_DEAD_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveDeadLetter(items: SyncItem[]): void {
  try {
    localStorage.setItem(SYNC_DEAD_KEY, JSON.stringify(items));
  } catch {
    /* quota exceeded */
  }
}

function makeBase(): SyncItemBase {
  return { enqueuedAt: Date.now(), retryCount: 0, _v: 1 };
}

export function enqueueContainerSync(
  sessionId: string,
  container: ContainerRecord,
  userId: string,
): void {
  if (!userId) {
    log.debug("Container sync skipped: no userId", {
      sessionId,
      containerId: container.containerId,
    });
    return;
  }

  const queue = loadQueue();
  const exists = queue.some(
    (item) =>
      item.type === "container" &&
      item.sessionId === sessionId &&
      item.container.containerId === container.containerId,
  );
  if (exists) {
    log.trace("Container already queued — skipping duplicate", {
      sessionId,
      containerId: container.containerId,
    });
    return;
  }
  queue.push({ ...makeBase(), type: "container", sessionId, container, userId });
  saveQueue(queue);
  log.debug("Container enqueued for sync", {
    sessionId,
    containerId: container.containerId,
    containerName: container.containerName,
  });
}

export function enqueueSessionSync(session: SessionHeader, userId: string): void {
  if (!userId) {
    log.debug("Session sync skipped: no userId", { sessionId: session.id });
    return;
  }

  const queue = loadQueue();
  const filtered = queue.filter(
    (item) => !(item.type === "session" && item.session.id === session.id),
  );
  filtered.push({ ...makeBase(), type: "session", session, userId });
  saveQueue(filtered);
  log.debug("Session enqueued for sync", {
    sessionId: session.id,
    status: session.status,
  });
}

export function enqueueIntentionSync(
  sessionId: string,
  record: IntentionRecord,
  userId: string,
): void {
  if (!userId || record.kind === 'unset') return;
  const queue = loadQueue();
  const filtered = queue.filter(
    (item) => !(item.type === "intention" && item.sessionId === sessionId),
  );
  filtered.push({
    ...makeBase(),
    type: "intention",
    sessionId,
    userId,
    intentionIds: record.kind === 'set' ? record.intentionIds : [],
    customText: record.kind === 'set' ? (record.customText ?? null) : null,
    kind: record.kind,
    setAt: record.kind === 'set' ? record.setAt : Date.now(),
  });
  saveQueue(filtered);
  log.debug("Intention enqueued for sync", { sessionId, kind: record.kind });
}

export function enqueueReflectionSync(
  sessionId: string,
  text: string | null,
  retreatCompletedAt: number,
  userId: string,
  audioUrl: string | null = null,
  photoUrls: string[] = [],
): void {
  if (!userId) return;
  const queue = loadQueue();
  const filtered = queue.filter(
    (item) => !(item.type === "reflection" && item.sessionId === sessionId),
  );
  filtered.push({
    ...makeBase(),
    type: "reflection",
    sessionId,
    userId,
    text,
    audioUrl,
    photoUrls,
    retreatCompletedAt,
  });
  saveQueue(filtered);
  log.debug("Reflection enqueued for sync", { sessionId });
}

export function enqueueSettingsSync(userId: string, settings: SettingsSnapshot): void {
  if (!userId) return;
  const queue = loadQueue();
  const filtered = queue.filter(
    (item) => !(item.type === "settings" && item.userId === userId),
  );
  const { bellsEnabled, ttsEnabled: voiceEnabled, whispersEnabled, preferredTradition, audioReflectionEnabled } = settings;
  filtered.push({
    ...makeBase(),
    type: "settings",
    userId,
    bellsEnabled,
    voiceEnabled,
    whispersEnabled,
    preferredTradition,
    audioReflectionEnabled,
  });
  // Track when settings were last written locally — used by pull() for conflict resolution
  try {
    localStorage.setItem(STORAGE_KEYS.SETTINGS_UPDATED_AT, String(Date.now()));
  } catch {
    /* quota */
  }
  saveQueue(filtered);
  log.debug("Settings enqueued for sync", { userId });
}

function transformSessionForUpsert(
  item: Extract<SyncItem, { type: "session" }>,
): Record<string, unknown> {
  return {
    id: item.session.id,
    user_id: item.userId,
    retreat_id: item.session.retreatId,
    retreat_name: item.session.retreatName,
    started_at: new Date(item.session.startedAt).toISOString(),
    ended_at: item.session.endedAt
      ? new Date(item.session.endedAt).toISOString()
      : null,
    status: item.session.status,
    updated_at: new Date().toISOString(),
  };
}

function transformContainerForUpsert(
  item: Extract<SyncItem, { type: "container" }>,
): Record<string, unknown> {
  return {
    session_id: item.sessionId,
    user_id: item.userId,
    container_id: item.container.containerId,
    container_name: item.container.containerName,
    container_type: item.container.containerType,
    container_posture: item.container.containerPosture ?? null,
    scheduled_duration_seconds: item.container.scheduledDurationSeconds,
    actual_duration_seconds: item.container.actualDurationSeconds,
    completed_at: new Date(item.container.completedAt).toISOString(),
    is_complete: item.container.isComplete,
  };
}

function transformSettingsForUpsert(
  item: SettingsSyncItem,
): Record<string, unknown> {
  return {
    user_id: item.userId,
    bells_enabled: item.bellsEnabled,
    voice_enabled: item.voiceEnabled,
    whispers_enabled: item.whispersEnabled,
    preferred_tradition: item.preferredTradition,
    audio_reflection_enabled: item.audioReflectionEnabled,
    updated_at: new Date().toISOString(),
  };
}

function transformIntentionForUpsert(
  item: IntentionSyncItem,
): Record<string, unknown> {
  return {
    session_id: item.sessionId,
    user_id: item.userId,
    intention_ids: item.intentionIds,
    custom_text: item.customText,
    kind: item.kind,
    set_at: new Date(item.setAt).toISOString(),
    updated_at: new Date().toISOString(),
  };
}

function transformReflectionForUpsert(
  item: ReflectionSyncItem,
): Record<string, unknown> {
  return {
    session_id: item.sessionId,
    user_id: item.userId,
    text: item.text,
    audio_url: item.audioUrl,
    photo_urls: item.photoUrls,
    retreat_completed_at: new Date(item.retreatCompletedAt).toISOString(),
    updated_at: new Date().toISOString(),
  };
}


let _draining = false;

// Tracks the currently authenticated user so drainSyncQueue can purge stale items
// from previous accounts even when called by SyncManager before syncLocalSessionsToRemote runs.
let _currentUserId: string | null = null;

export function setCurrentSyncUserId(userId: string | null): void {
  _currentUserId = userId;
}

export async function drainSyncQueue(): Promise<void> {
  if (_draining) {
    log.trace("drainSyncQueue: drain already in progress — skipping");
    return;
  }

  // Purge items from a previous user before draining. drainSyncQueue is called by
  // SyncManager on network restore / visibility, independently of syncLocalSessionsToRemote,
  // so the guard must live here too.
  let queue = loadQueue();
  if (_currentUserId) {
    const owned = queue.filter((item) => item.userId === _currentUserId);
    if (owned.length < queue.length) {
      log.info("drainSyncQueue: purging stale items from previous user", { count: queue.length - owned.length });
      saveQueue(owned);
      queue = owned;
    }
  }

  if (queue.length === 0) return;

  // Cap: if queue is oversized, drop oldest items first
  if (queue.length > MAX_QUEUE_SIZE) {
    log.warn("drainSyncQueue: queue exceeds cap — dropping oldest items", {
      size: queue.length,
      cap: MAX_QUEUE_SIZE,
    });
    saveQueue(queue.slice(queue.length - MAX_QUEUE_SIZE));
  }

  _draining = true;
  let client: ReturnType<typeof getClient>;
  try {
    client = getClient();
  } catch {
    log.warn("drainSyncQueue: Supabase client unavailable — skipping drain");
    _draining = false;
    return;
  }

  const remaining: SyncItem[] = [];
  const dead: SyncItem[] = [];
  let synced = 0;

  for (const item of queue) {
    const retryCount = item.retryCount ?? 0;
    if (retryCount >= MAX_RETRY_COUNT) {
      log.warn("drainSyncQueue: item exceeded max retries — moving to dead-letter", {
        type: item.type,
        retryCount,
      });
      dead.push(item);
      continue;
    }

    try {
      let table: string;
      let data: Record<string, unknown>;
      let conflict: string;

      if (item.type === "session") {
        table = SUPABASE_SESSIONS_TABLE;
        data = transformSessionForUpsert(item);
        conflict = CONFLICT_CONSTRAINT_SESSION;
      } else if (item.type === "container") {
        table = SUPABASE_CONTAINERS_TABLE;
        data = transformContainerForUpsert(item);
        conflict = CONFLICT_CONSTRAINT_CONTAINER;
      } else if (item.type === "intention") {
        table = SUPABASE_INTENTIONS_TABLE;
        data = transformIntentionForUpsert(item);
        conflict = CONFLICT_CONSTRAINT_INTENTION;
      } else if (item.type === "reflection") {
        table = SUPABASE_REFLECTIONS_TABLE;
        data = transformReflectionForUpsert(item);
        conflict = CONFLICT_CONSTRAINT_REFLECTION;
      } else {
        table = SUPABASE_SETTINGS_TABLE;
        data = transformSettingsForUpsert(item);
        conflict = CONFLICT_CONSTRAINT_SETTINGS;
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error: upsertError } = await client
        .from(table)
        .upsert(data as any, { onConflict: conflict });
      if (upsertError) {
        throw new Error(`${upsertError.code ?? "error"}: ${upsertError.message}`);
      }

      log.debug(`${item.type} synced to Supabase`, { id: getItemId(item) });
      synced++;
    } catch (err) {
      log.warn("Sync item failed — will retry", {
        type: item.type,
        id: getItemId(item),
        error: String(err),
      });
      remaining.push({ ...item, retryCount: retryCount + 1 });
    }
  }

  if (dead.length > 0) {
    const existing = loadDeadLetter();
    saveDeadLetter([...existing, ...dead]);
  }

  log.info("Sync queue drained", { synced, failed: remaining.length, dead: dead.length });
  saveQueue(remaining);
  _draining = false;
}

/**
 * Retroactively enqueue every session in localStorage for a newly authenticated user.
 * Covers sessions that were recorded while offline or before the user logged in.
 * Upserts are idempotent — safe to call multiple times.
 *
 * Ownership safety: sessions stored in localStorage carry no userId field, so we
 * use LAST_USER_ID to determine whether they belong to the current user.
 *   - lastUser === null  → device never tracked users; skip (can't confirm ownership)
 *   - lastUser !== user  → different account switched in; clear stale data and skip
 *   - lastUser === user  → same account; safe to enqueue
 */
export async function syncLocalSessionsToRemote(userId: string): Promise<void> {
  // Register current user so drainSyncQueue always has a valid reference.
  _currentUserId = userId;

  // Purge stale queue items unconditionally — must run before any early return.
  let queue = loadQueue();
  const owned = queue.filter((item) => item.userId === userId);
  if (owned.length < queue.length) {
    log.info('syncLocalSessionsToRemote: dropping stale queue items from different user', { count: queue.length - owned.length });
    queue = owned;
    saveQueue(queue);
  }

  // Ownership check: sessions have no userId in localStorage, so we infer ownership
  // from the last recorded user. If ownership is uncertain, skip retroactive enqueue
  // to prevent another user's sessions from being attributed to this account.
  let lastUserId: string | null = null;
  try { lastUserId = localStorage.getItem(STORAGE_KEYS.LAST_USER_ID); } catch { /* ignore */ }
  try { localStorage.setItem(STORAGE_KEYS.LAST_USER_ID, userId); } catch { /* quota */ }

  if (lastUserId === null) {
    // First time tracking users on this device — skip retroactive sync.
    // Sessions created here have unknown ownership; inline enqueueSessionSync
    // calls during active sessions are the authoritative sync path.
    log.info('syncLocalSessionsToRemote: no prior user tracked — skipping retroactive sync');
    return;
  }

  if (lastUserId !== userId) {
    // Different account switched in. Clear stale local sessions so history is clean,
    // then skip — SyncManager.pull() will restore this user's sessions from DB.
    log.info('syncLocalSessionsToRemote: account switch detected — clearing stale local sessions', { from: lastUserId.slice(0, 8) });
    clearLocalSessionData();
    return;
  }

  const sessions = getAllSessions();
  if (sessions.length === 0) return;

  log.info('Retroactive sync: enqueuing all local sessions', { count: sessions.length, userId });

  // Cap check before bulk enqueue
  if (queue.length + sessions.length * 2 > MAX_QUEUE_SIZE) {
    log.warn('syncLocalSessionsToRemote: queue near cap — trimming oldest to make room');
    queue = queue.slice(Math.max(0, queue.length - Math.floor(MAX_QUEUE_SIZE / 2)));
    saveQueue(queue);
  }

  for (const session of sessions) {
    const header: SessionHeader = {
      id: session.id,
      retreatId: session.retreatId,
      retreatName: session.retreatName,
      startedAt: session.startedAt,
      endedAt: session.endedAt,
      status: session.status,
    };
    enqueueSessionSync(header, userId);
    for (const container of session.containers) {
      enqueueContainerSync(session.id, container, userId);
    }
  }

  await drainSyncQueue();
}
